'use client';

import { Image } from '@packages/shared/src/ui/Image/Image';
import { Skeleton } from '@packages/shared/src/ui/Skeleton/Skeleton';
import { defineMessages, useIntl } from 'react-intl';
import { useState } from 'react';
import type { MessageValue } from 'react-intl';

const messages = defineMessages<{ readonly moreProductsLabel: { readonly count: MessageValue } }>({
  moreProductsLabel: {
    id: 'ProductImageRowList.MoreProductsLabel',
    defaultMessage: '+ {count} Artikel',
  },
});

// Module-level cache to track loaded images across all component instances
const loadedImagesCache = new Set<string>();

export type ProductItem = {
  imageUrl: string | null;
  name: string;
  url: string | null;
};

export type ProductImageRowListProps = {
  /** Array of product items to display */
  items: ProductItem[];
  /** Total count of additional items beyond visible ones */
  additionalItemsCount?: number;
  /** Prefix for generating unique keys */
  keyPrefix?: string;
  /** Optional className for image container size */
  imageSize?: 'small' | 'large';
};

type ProductImageProps = {
  imageUrl: string;
  name: string;
  sizeClasses: string;
};

const ProductImage = ({ imageUrl, name, sizeClasses }: ProductImageProps) => {
  // Initialize with cached state - if image was loaded before, skip skeleton
  const [isLoaded, setIsLoaded] = useState(loadedImagesCache.has(imageUrl));

  const handleLoad = () => {
    loadedImagesCache.add(imageUrl);
    setIsLoaded(true);
  };

  return (
    <div className={`relative shrink-0 ${sizeClasses}`}>
      {!isLoaded && (
        <Skeleton className="bg-color-background-base absolute inset-0 h-full w-full" />
      )}
      <Image src={imageUrl} alt={name} fill className="object-contain" onLoad={handleLoad} />
    </div>
  );
};

/**
 * ProductImageList displays a horizontal list of product images with optional additional count.
 * Supports skeleton loading and responsive sizing.
 */
export const ProductImageRowList = ({
  items,
  imageSize = 'small',
  additionalItemsCount = 0,
  keyPrefix = 'product',
}: ProductImageRowListProps) => {
  const { formatMessage } = useIntl();
  const visibleItems = items.slice(0, 3);

  // Determine image size classes based on variant
  const sizeClasses =
    imageSize === 'large' ? 'h-[64px] w-[64px] md:h-[120px] md:w-[120px]' : 'h-[64px] w-[64px]';
  const gapClasses = imageSize === 'large' ? 'gap-2 md:gap-2.5' : 'gap-2';

  return (
    <div className={`flex items-center ${gapClasses}`}>
      {visibleItems.map((item, index) => (
        <div key={`${keyPrefix}-${item.imageUrl || index}`}>
          <ProductImage imageUrl={item.imageUrl || ''} name={item.name} sizeClasses={sizeClasses} />
        </div>
      ))}
      {additionalItemsCount > 0 && (
        <p className="text-color-text-dark text-size-body-sm">
          {formatMessage(messages.moreProductsLabel, {
            count: additionalItemsCount,
          })}
        </p>
      )}
    </div>
  );
};
